import { createContext, FC, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getShortcutKeysMap } from './ShortcutsKeymap'
import { KeyCodeToKey, ShortcutsCategories, ShortcutType, SpecKeys } from './Shortcuts.types'

interface ShortcutsContextProps {
    registerCategory: (category: ShortcutsCategories, shortcuts: ShortcutType) => void
    unregisterCategory: (category: ShortcutsCategories, shortcutKeys?: string[]) => void
}

const ShortcutsContext = createContext<ShortcutsContextProps | undefined>(undefined)

export const useShortcutsContext = (): ShortcutsContextProps => {
    const context = useContext(ShortcutsContext)
    if (!context) {
        throw new Error('useShortcuts must be used within a ShortcutsProvider')
    }
    return context
}

const getShortcutKey = (event: KeyboardEvent): string => {
    let keys = []
    if (event.altKey) keys.push('Alt')
    if (event.ctrlKey) keys.push('Control')
    if (event.metaKey) keys.push('Meta')
    if (event.shiftKey) keys.push('Shift')
    keys.push(KeyCodeToKey[event.code])
    return keys.join('+')
}

const sortRegisteringKey = (key: string): string => {
    const keys = key.split('+')
    const result: string[] = []
    if (keys.includes(SpecKeys.ALT)) {
        result.push(SpecKeys.ALT)
    }
    if (keys.includes(SpecKeys.CONTROL)) {
        result.push(SpecKeys.CONTROL)
    }
    if (keys.includes(SpecKeys.META)) {
        result.push(SpecKeys.META)
    }
    if (keys.includes(SpecKeys.SHIFT)) {
        result.push(SpecKeys.SHIFT)
    }
    result.push(keys[keys.length - 1])
    return result.join('+')
}

const ShortcutsProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [shortcuts, setShortcuts] = useState<Record<string, Record<string, ((event: KeyboardEvent) => void)[]>>>({})
    const [isInputFocused, setIsInputFocused] = useState(false)

    const registerCategory = useCallback((category: ShortcutsCategories, newShortcuts: ShortcutType) => {
        setShortcuts(prevShortcuts => {
            const existingCategory = prevShortcuts[category] || {}
            const updatedCategory = { ...existingCategory }

            Object.keys(newShortcuts).forEach((key: string) => {
                const actualKey = getShortcutKeysMap()[category]?.[key]
                if (actualKey) {
                    const sortedKey = sortRegisteringKey(actualKey)
                    const callback = newShortcuts[key]
                    if (updatedCategory[sortedKey]) {
                        if (!updatedCategory[sortedKey].includes(callback)) {
                            updatedCategory[sortedKey] = [...updatedCategory[sortedKey], callback]
                        }
                    } else {
                        updatedCategory[sortedKey] = [callback]
                    }
                }
            })

            return {
                ...prevShortcuts,
                [category]: updatedCategory
            }
        })
    }, [])

    const unregisterCategory = useCallback((category: ShortcutsCategories, shortcutKeys?: string[]) => {
        setShortcuts(prevShortcuts => {
            if (!category || !(category in prevShortcuts)) {
                return prevShortcuts
            }

            if (!shortcutKeys?.length) {
                const { [category]: _, ...rest } = prevShortcuts
                return rest
            }

            const keyMap = getShortcutKeysMap()[category] || {}
            const sortedKeysToRemove = new Set(
                shortcutKeys
                    .map((key) => keyMap[key])
                    .filter(Boolean)
                    .map((key) => sortRegisteringKey(key))
            )

            const updatedCategory = { ...prevShortcuts[category] }
            sortedKeysToRemove.forEach((sortedKey) => {
                delete updatedCategory[sortedKey]
            })

            if (Object.keys(updatedCategory).length === 0) {
                const { [category]: _, ...rest } = prevShortcuts
                return rest
            }

            return {
                ...prevShortcuts,
                [category]: updatedCategory
            }
        })
    }, [])

    const handleKeydown = useCallback((event: KeyboardEvent) => {
        const hasOpenDialog = !!document.querySelector('[role="dialog"]')
        if (!isInputFocused && !hasOpenDialog) {
            const key = getShortcutKey(event)
            for (const category in shortcuts) {
                const callbackArr = shortcuts[category][key]
                if (callbackArr && callbackArr.length > 0) {
                    callbackArr.forEach(callback => {
                        callback(event)
                    })
                    break
                }
            }
        }
    }, [shortcuts, isInputFocused])

    useEffect(() => {
        window.addEventListener('keydown', handleKeydown)
        return () => {
            window.removeEventListener('keydown', handleKeydown)
        }
    }, [handleKeydown])

    useEffect(() => {
        const handleFocusIn = (event: any) => {
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                setIsInputFocused(true)
            }
        }

        const handleFocusOut = (event: any) => {
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                setIsInputFocused(false)
            }
        }

        window.addEventListener('focusin', handleFocusIn)
        window.addEventListener('focusout', handleFocusOut)

        return () => {
            window.removeEventListener('focusin', handleFocusIn)
            window.removeEventListener('focusout', handleFocusOut)
        }
    }, [])

    const contextValue = useMemo(() => ({
        registerCategory,
        unregisterCategory
    }), [registerCategory, unregisterCategory])

    return (
        <ShortcutsContext.Provider value={contextValue}>
            {children}
        </ShortcutsContext.Provider>
    )
}

export default memo(ShortcutsProvider)